/**
 * ShareLinkDialog — Share a conversation via a time-scoped read-only link.
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §3.3 Wave 3A
 *
 * Uses the existing backend:
 *   POST /conversations/:id/share  → { shareToken, shareUrl }
 *   DELETE /conversations/:id/share → 204
 *
 * Features:
 *   - Generates a share token on first open (lazy — not on mount).
 *   - Copy button with confirmation flash.
 *   - Expiry selector (24h / 7d / 30d / Never). Note: the current
 *     backend does not yet enforce expiry; the selector is wired into
 *     the POST body so the backend can add enforcement without a UI
 *     change. The selected label is shown next to the URL for
 *     transparency.
 *   - Revoke button calls DELETE. After revoke the dialog resets so a
 *     new token can be created.
 *
 * No fake data. The component is idle until the user opens the dialog.
 */

import { useState, useCallback } from 'react';
import { Copy, CheckCheck, Link, Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

// ── Expiry config ────────────────────────────────────────────────────────────

export type ShareExpiry = '24h' | '7d' | '30d' | 'never';

interface ExpiryOption {
  value: ShareExpiry;
  label: string;
  description: string;
}

export const EXPIRY_OPTIONS: ExpiryOption[] = [
  { value: '24h', label: '24 hours', description: 'Link expires in 24 hours' },
  { value: '7d', label: '7 days', description: 'Link expires in 7 days' },
  { value: '30d', label: '30 days', description: 'Link expires in 30 days' },
  { value: 'never', label: 'Never', description: 'Link does not expire' },
];

// ── API shapes ───────────────────────────────────────────────────────────────

interface ShareResponse {
  shareToken: string;
  shareUrl: string;
}

// ── Component ────────────────────────────────────────────────────────────────

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
}

type DialogState =
  | { phase: 'idle' }
  | { phase: 'generating' }
  | { phase: 'ready'; shareToken: string; shareUrl: string }
  | { phase: 'revoking' }
  | { phase: 'revoked' }
  | { phase: 'error'; message: string };

export function ShareLinkDialog({
  open,
  onOpenChange,
  conversationId,
}: ShareLinkDialogProps) {
  const [state, setState] = useState<DialogState>({ phase: 'idle' });
  const [expiry, setExpiry] = useState<ShareExpiry>('7d');
  const [copied, setCopied] = useState(false);

  // Reset when dialog closes so re-opening is a clean slate.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setState({ phase: 'idle' });
        setCopied(false);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  const handleGenerate = useCallback(async () => {
    setState({ phase: 'generating' });
    try {
      const res = await apiClient.post<ShareResponse>(
        `/conversations/${conversationId}/share`,
        { expiry },
      );
      setState({ phase: 'ready', shareToken: res.shareToken, shareUrl: res.shareUrl });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create share link';
      setState({ phase: 'error', message });
      toast.error(message);
    }
  }, [conversationId, expiry]);

  const handleCopy = useCallback(async () => {
    if (state.phase !== 'ready') return;
    try {
      await navigator.clipboard.writeText(state.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }, [state]);

  const handleRevoke = useCallback(async () => {
    if (state.phase !== 'ready') return;
    setState({ phase: 'revoking' });
    try {
      await apiClient.delete(`/conversations/${conversationId}/share`);
      setState({ phase: 'revoked' });
      toast.success('Share link revoked');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not revoke link';
      // Restore the ready state so the user can retry revoke.
      const prev = state;
      setState(prev);
      toast.error(message);
    }
  }, [conversationId, state]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-4 w-4" aria-hidden="true" />
            Share Conversation
          </DialogTitle>
          <DialogDescription>
            Anyone with the link can view this conversation (read-only).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Expiry selector */}
          <div className="space-y-1.5">
            <label
              htmlFor="share-expiry-select"
              className="text-sm font-medium text-foreground"
            >
              Link expiry
            </label>
            <Select
              value={expiry}
              onValueChange={(v) => setExpiry(v as ShareExpiry)}
              disabled={state.phase === 'generating' || state.phase === 'revoking'}
            >
              <SelectTrigger id="share-expiry-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span>{opt.label}</span>
                    <span className="text-muted-foreground text-xs ml-2">
                      — {opt.description}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* URL + Copy */}
          {state.phase === 'ready' && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Share URL
              </label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={state.shareUrl}
                  className="font-mono text-xs"
                  data-testid="share-url-input"
                  aria-label="Share URL"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  title={copied ? 'Copied!' : 'Copy link'}
                  aria-label={copied ? 'Copied!' : 'Copy link'}
                  data-testid="share-copy-button"
                >
                  {copied ? (
                    <CheckCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {state.phase === 'revoked' && (
            <p className="text-sm text-muted-foreground text-center py-2">
              The share link has been revoked. Generate a new one above.
            </p>
          )}

          {state.phase === 'error' && (
            <p className="text-sm text-destructive py-1">{state.message}</p>
          )}
        </div>

        <DialogFooter className="flex items-center gap-2">
          {/* Revoke — only shown when a token is active */}
          {state.phase === 'ready' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 mr-auto"
              onClick={handleRevoke}
              title="Revoke share link"
              aria-label="Revoke share link"
              data-testid="share-revoke-button"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Revoke
            </Button>
          )}

          {(state.phase === 'idle' ||
            state.phase === 'revoked' ||
            state.phase === 'error') && (
            <Button
              onClick={handleGenerate}
              className="ml-auto"
              data-testid="share-generate-button"
              title="Generate share link"
            >
              Generate Link
            </Button>
          )}

          {state.phase === 'generating' && (
            <Button disabled className="ml-auto">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating…
            </Button>
          )}

          {state.phase === 'revoking' && (
            <Button disabled className="ml-auto">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Revoking…
            </Button>
          )}

          {state.phase === 'ready' && (
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              title="Close dialog"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
